import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import MachineAPI, { GetAssetListResponse } from '~/api/machine-api';
import { getFilters } from '~/ducks/machine-filter-v2/selectors';
import { type Status } from '@fleet-bffs/api-contract';
import type { AssetListItem } from '~/data/AssetListItem/AssetListItem';
import { getFilterMapFromFilters } from '~/ducks/machine-filter-v2/utils/filter-utils';
import * as roboticMowerUpdateActions from '~/ducks/asset-updates/actions';
import createCaughtAsyncThunk from '~/utils/create-action/create-caught-async-thunk';
import mergeDeep from '~/utils/merge-deep/merge-deep';
import { exportFilters } from '~/domain/inventory-list/utils/list-filter-utils';
import { type BatchCommandResultNotification, defaultState, Fields } from '../data/AssetListRecord';
import { baseKeys } from '../data/AssetListItem';
import { addHandlerForTagUpdates } from './reducerHelpers/handleTagUpdates';
import { findAssetIndex } from './asset-list-selectors';

type UpdateAssetStatusAction = PayloadAction<
  Omit<ReturnType<typeof roboticMowerUpdateActions.updateAssetStatus>['payload'], 'status'> & {
    status: Status;
  }
>;

const updateAssetListState = (state: Fields, machines: AssetListItem[]) => {
  state.isLoading = false;
  state.forceReload = false;
  state.selectedAll = false;

  //@ts-ignore
  state.assets = machines.map((machine) => ({
    ...machine,
    ...baseKeys,
  }));

  if (state.selectedAssetIds.length > 0) {
    const assetIds = new Set(machines.map((asset) => asset.assetDetails.id));

    state.selectedAssetIds = state.selectedAssetIds.filter((selectedId) =>
      assetIds.has(selectedId)
    );

    state.highlightedAssetIds = state.highlightedAssetIds.filter((selectedId) =>
      assetIds.has(selectedId)
    );
  }
};

export const getAssetList = createCaughtAsyncThunk<GetAssetListResponse, boolean>(
  'getAssetList',
  async (limited, thunkAPI) => {
    const response = await (limited
      ? MachineAPI.getAssetList(exportFilters(thunkAPI.getState().assetListFilter.filtersValues))
      : MachineAPI.getAssetListV3(
          getFilterMapFromFilters(getFilters(thunkAPI.getState().machineFilterV2))
        ));

    return response;
  }
);

const slice = createSlice({
  name: 'asset-list',
  initialState: defaultState,
  reducers: {
    openUnmountSensorWarning: (state, action) => {
      state.openUnmountSensorWarning = action.payload;
    },
    setForceReload: (state) => {
      state.forceReload = true;
    },
    consumeForceReload: (state) => {
      state.forceReload = false;
    },

    clearAssetList: () => {
      return defaultState;
    },

    toggleAssetSelected: (state, action: PayloadAction<AssetId>) => {
      const isSelected =
        state.selectedAll || state.selectedAssetIds.some((id) => id === action.payload);

      if (isSelected) {
        state.selectedAssetIds = state.selectedAssetIds.filter(
          (assetId) => assetId !== action.payload
        );
        if (state.dashboardMapClusterSelected?.assetIds?.length) {
          const filteredDashboardSelectedAssets = state.dashboardMapClusterSelected.assetIds.filter(
            (id) => id !== action.payload
          );
          if (filteredDashboardSelectedAssets.length === 0) {
            state.dashboardMapClusterSelected = null;
          } else {
            state.dashboardMapClusterSelected.assetIds = filteredDashboardSelectedAssets;
          }
        }

        state.highlightedAssetIds = state.highlightedAssetIds.filter(
          (highlightedAssetId) => highlightedAssetId !== action.payload
        );
        state.selectedAll = false;
      } else {
        state.selectedAssetIds.push(action.payload);
      }
    },

    addBatchCommandResultNotification: (
      state,
      action: PayloadAction<BatchCommandResultNotification>
    ) => {
      state.batchCommandsResultsNotifications.push(action.payload);
    },

    resetBatchCommandResultNotification: (
      state,
      action: PayloadAction<BatchCommandResultNotification>
    ) => {
      state.batchCommandsResultsNotifications = state.batchCommandsResultsNotifications.filter(
        (item) => {
          return !(
            item.hfsActionId === action.payload.hfsActionId &&
            item.iprIds.join('-') === action.payload.iprIds.join('-')
          );
        }
      );
    },

    toggleAssetsHighlighted: (state, action: PayloadAction<AssetId[]>) => {
      const alreadySelected =
        state.selectedAll ||
        state.selectedAssetIds.some((selectedIprId) => action.payload.includes(selectedIprId));
      if (alreadySelected) {
        state.highlightedAssetIds = state.highlightedAssetIds.filter(
          (assetId) => !action.payload.includes(assetId)
        );
        state.selectedAll = false;
      } else {
        state.highlightedAssetIds.push(
          ...action.payload.filter((assetId) => !state.highlightedAssetIds.includes(assetId))
        );
      }
    },

    setDashboardMapClusterSelected: (
      state,
      action: PayloadAction<{
        assetIds: AssetId[];
        scrollTo?: boolean;
        zoomLevel?: number;
        clusterId?: number;
        keepSelection?: boolean;
      }>
    ) => {
      if (!action.payload.keepSelection) {
        state.selectedAssetIds = action.payload.assetIds;
      }
      state.dashboardMapClusterSelected = {
        scrollTo: !!action.payload.scrollTo,
        assetIds: action.payload.assetIds,
        zoomLevel: action.payload.zoomLevel ?? 20,
        clusterId: action.payload.clusterId,
      };
      state.highlightedAssetIds = [...state.highlightedAssetIds, ...action.payload.assetIds];
    },

    toggleAssetsSelected: (
      state,
      action: PayloadAction<{ assetIds: AssetId[]; scrollTo?: boolean }>
    ) => {
      const allIsSelected = action.payload.assetIds.every((assetId) =>
        state.selectedAssetIds.includes(assetId)
      );

      if (allIsSelected) {
        state.selectedAssetIds = state.selectedAssetIds.filter((assetId) => {
          return !action.payload.assetIds.includes(assetId);
        });
      } else {
        const toAdd = action.payload.assetIds.filter(
          (assetId) => !state.selectedAssetIds.includes(assetId)
        );
        state.selectedAssetIds = state.selectedAssetIds.concat(toAdd);
      }
      state.selectedAll = false;
      state.scrollToSelected = action.payload.scrollTo === true;
    },

    scrollToSelected: (state, action: PayloadAction<boolean>) => {
      state.scrollToSelected = action.payload;
    },

    setAssetsSelected: (
      state,
      action: PayloadAction<{ assetIds: AssetId[]; selected: boolean }>
    ) => {
      if (!action.payload.selected) {
        state.selectedAssetIds = state.selectedAssetIds.filter((assetId) => {
          return !action.payload.assetIds.includes(assetId);
        });

        if (state.dashboardMapClusterSelected?.assetIds?.length) {
          state.dashboardMapClusterSelected = null;
        }

        state.highlightedAssetIds = state.highlightedAssetIds.filter(
          (highlightedAssetId) => !action.payload.assetIds.includes(highlightedAssetId)
        );
      } else {
        const toAdd = action.payload.assetIds.filter(
          (assetId) => !state.selectedAssetIds.includes(assetId)
        );
        state.selectedAssetIds = state.selectedAssetIds.concat(toAdd);
      }
    },

    setAllSelected: (state, action: PayloadAction<boolean>) => {
      state.selectedAll = action.payload;
    },

    resetAssetsSelection: (state) => {
      state.highlightedAssetIds = [];
      state.selectedAssetIds = [];
      state.dashboardMapClusterSelected = null;
      state.batchCommandsSelectionIndication = false;
      state.selectedAll = false;
    },

    setAssetName: (state, action: PayloadAction<{ assetId: AssetId; name: string }>) => {
      const index = findAssetIndex(state.assets, action.payload.assetId);

      if (index === -1) {
        return;
      }

      state.assets[index].assetDetails.name = action.payload.name;
    },

    setBatchCommandsSelectionIndicationStatus: (state, action: PayloadAction<boolean>) => {
      state.batchCommandsSelectionIndication = action.payload;
    },

    setAssetListFromAPI: (state, action: PayloadAction<{ machines: AssetListItem[] }>) => {
      updateAssetListState(state, action.payload.machines);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAssetList.pending, (state) => {
        state.isLoading = true;
        state.forceReload = false;
        delete state.error;
      })
      .addCase(getAssetList.fulfilled, (state, action) => {
        updateAssetListState(state, action.payload.machines);
      })
      .addCase(getAssetList.rejected, (state, action) => {
        state.isLoading = false;
        if (action.meta.aborted) {
          return;
        }

        state.error = action.payload;
      });

    builder.addCase(
      roboticMowerUpdateActions.updateAsset.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAsset>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1) {
          return;
        }
        // @ts-expect-error -- pinned local-vs-BFF AssetListItem divergence
        state.assets[index] = {
          ...state.assets[index],
          ...action.payload.asset,
        };
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.partialUpdateAsset.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.partialUpdateAsset>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1) {
          return;
        }
        state.assets[index] = mergeDeep(state.assets[index], action.payload.asset);
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.updateAssetStatus.type,
      (state, action: UpdateAssetStatusAction) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1) {
          return;
        }
        state.assets[index].status = action.payload.status;
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.updateAssetPower.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAssetPower>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1 || !state.assets[index].machineData) {
          return;
        }
        state.assets[index].machineData.power = action.payload.power;
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.updateAssetFirmware.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAssetFirmware>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1 || !state.assets[index].firmware) {
          return;
        }
        state.assets[index].firmware.version = action.payload.version;
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.updateAssetCSFirmware.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAssetCSFirmware>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1 || !state.assets[index].roboticMower) {
          return;
        }
        state.assets[index].roboticMower.chargingStationFirmwareVersion = action.payload.version;
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.updateAssetLocation.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAssetLocation>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1 || !state.assets[index].machineData) {
          return;
        }
        state.assets[index].machineData.location = action.payload.location;
      }
    );

    builder.addCase(
      roboticMowerUpdateActions.updateAssetConnectivity.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAssetConnectivity>) => {
        const index = findAssetIndex(state.assets, action.payload.assetId);
        if (index === -1 || !state.assets[index].machineData) {
          return;
        }
        state.assets[index].machineData.connectivity = action.payload.connectivity;
        if (state.assets[index].roboticMower?.availableSettings) {
          state.assets[index].roboticMower.validSettings = action.payload.connectivity.connected
            ? state.assets[index].roboticMower.availableSettings
            : [];
        }
      }
    );

    addHandlerForTagUpdates(builder);
  },
});

export const {
  clearAssetList,
  setAssetName,
  setAllSelected,
  setAssetsSelected,
  toggleAssetsSelected,
  setDashboardMapClusterSelected,
  toggleAssetSelected,
  toggleAssetsHighlighted,
  setForceReload,
  consumeForceReload,
  scrollToSelected,
  openUnmountSensorWarning,
  resetAssetsSelection,
  addBatchCommandResultNotification,
  resetBatchCommandResultNotification,
  setBatchCommandsSelectionIndicationStatus,
  setAssetListFromAPI,
} = slice.actions;
export default slice.reducer;
